import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface PageHeaderProps {
  title: string
  /** One line. What this screen is for, not what the product is. */
  description?: ReactNode
  actions?: ReactNode
  /** Sits beside the title — a count, a status, a live indicator. */
  meta?: ReactNode
  className?: string
}

/**
 * The top of a route.
 *
 * Every screen used to open straight onto its content, so nothing said where
 * you were or what you could do here — the tab strip in the browser was the
 * only page title in the product. One header, one title level, one place for
 * route-level actions.
 */
export function PageHeader({
  title,
  description,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex flex-wrap items-start justify-between gap-x-4 gap-y-3 pb-1',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-xl leading-tight font-semibold tracking-[-0.015em] text-ink">
            {title}
          </h1>
          {meta}
        </div>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  )
}
